import { lazy, memo, Suspense, useCallback, useMemo } from 'react';

import './version.css';

import Portal from './Portal';
import VersionTag from './VersionTag';
import { getVersionPopoverStackContainer, getVersionStackContainer } from './versionStack';
import { useBoolean } from '../../hooks/useBoolean';
import { useRandomInterval } from '../../hooks/useRandomInterval';
import { useVersionStatus, type VersionStatus } from '../../hooks/useVersionStatus';

const Toasty = lazy(() => import('./Toasty'));
const UpdatePopover = lazy(() => import('./UpdatePopover'));

const DEFAULT_NAG_MIN_MS = 5_000;
const DEFAULT_NAG_MAX_MS = 30_000;
const MIN_INTERVAL_FLOOR_MS = 1_000;
const MAX_FREQUENCY_MULTIPLIER = 5;

const DISMISSIBLE_STATUSES: VersionStatus[] = ['up-to-date', 'behind-patch'];

const STATUS_STYLE: Record<VersionStatus, { emoji: string; variantClassName: string }> = {
  'up-to-date': { emoji: '🏆', variantClassName: 'eduzz-ui-version-tag--up-to-date' },
  'behind-patch': { emoji: '🏅', variantClassName: 'eduzz-ui-version-tag--behind-patch' },
  'behind-minor': { emoji: '⚠️', variantClassName: 'eduzz-ui-version-tag--behind-minor' },
  'behind-major': { emoji: '🦖', variantClassName: 'eduzz-ui-version-tag--behind-major' }
};

export interface VersionProps {
  currentVersion: string;
  packageName: string;
  enableNag?: boolean;
  nagMinMs?: number;
  nagMaxMs?: number;
  showVersion?: boolean;
}

const Version = ({
  currentVersion,
  packageName,
  enableNag = true,
  nagMinMs = DEFAULT_NAG_MIN_MS,
  nagMaxMs = DEFAULT_NAG_MAX_MS,
  showVersion = false
}: VersionProps) => {
  const slug = packageName.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');
  const latestUrl = `https://registry.npmjs.org/${packageName}/latest`;
  const etagStorageKey = `${slug}-version-etag`;
  const valueStorageKey = `${slug}-version-value`;

  const { status, latestVersion, versionsBehind } = useVersionStatus({
    currentVersion,
    latestUrl,
    etagStorageKey,
    valueStorageKey
  });

  const stackContainer = useMemo(() => getVersionStackContainer(), []);
  const popoverStackContainer = useMemo(() => getVersionPopoverStackContainer(), []);

  const multiplier = Math.min(Math.max(versionsBehind, 1), MAX_FREQUENCY_MULTIPLIER);
  const scaledMinMs = Math.max(Math.round(nagMinMs / multiplier), MIN_INTERVAL_FLOOR_MS);
  const scaledMaxMs = Math.max(Math.round(nagMaxMs / multiplier), scaledMinMs);

  const [modalOpen, , openModal, closeModal] = useBoolean(false);
  const [popoverVisible, , showPopover, hidePopover] = useBoolean(false);

  useRandomInterval(openModal, {
    minMs: scaledMinMs,
    maxMs: scaledMaxMs,
    enabled: enableNag && status === 'behind-major'
  });

  useRandomInterval(showPopover, {
    minMs: scaledMinMs,
    maxMs: scaledMaxMs,
    enabled: enableNag && (status === 'behind-minor' || status === 'behind-major')
  });

  const handleClosePopover = useCallback(hidePopover, [hidePopover]);

  if (!status || !latestVersion) {
    return null
  }

  const isDismissible = DISMISSIBLE_STATUSES.includes(status);

  if (isDismissible && !showVersion) {
    return null;
  }

  const { emoji, variantClassName } = STATUS_STYLE[status];

  return (
    <>
      <Portal target={stackContainer}>
        <VersionTag
          packageName={packageName}
          version={currentVersion}
          variantClassName={variantClassName}
          emoji={emoji}
          title={`Versão atual: v${currentVersion}\nVersão desejada: v${latestVersion}`}
        />
      </Portal>

      {(status === 'behind-minor' || status === 'behind-major') && (
        <Portal target={popoverStackContainer}>
          <Suspense fallback={null}>
            <UpdatePopover
              visible={popoverVisible}
              currentVersion={currentVersion}
              latestVersion={latestVersion}
              packageName={packageName}
              onClose={handleClosePopover}
            />
          </Suspense>
        </Portal>
      )}

      {status === 'behind-major' && (
        <Suspense fallback={null}>
          <Toasty open={modalOpen} onDone={closeModal} />
        </Suspense>
      )}
    </>
  );
};

export default memo(Version);
